import React from "react";
import styled from "styled-components";
import type { Profile } from "../types";
import { itensDeCompletude, resumoDeHoje } from "./editor-chrome-helpers";

/*
 * Peças visuais das pranchetas do editor (PerfilBasico, PerfilContato, PerfilLocal,
 * PerfilDescricao): grade "conteúdo + aside de 380px", cartões do aside, dicas,
 * campos com prefixo, chips removíveis e selos. Só apresentação.
 */

export const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 24px;
  align-items: start;
  @media (max-width: 1180px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Coluna = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`;

export const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const AsideCard = styled.div`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
`;

export const Lab = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #3c4043;
  text-transform: uppercase;
  .material-icons {
    font-size: 18px;
    color: #5f6368;
  }
`;

export const Hint = styled.span`
  font-size: 12px;
  line-height: 17px;
  color: #5f6368;
`;

export const Tip = styled.div<{ $tom?: "neutro" | "aviso" }>`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${(p) => (p.$tom === "aviso" ? "#FEF7E0" : "#F8F9FA")};
  color: ${(p) => (p.$tom === "aviso" ? "#7A4B00" : "#3C4043")};
  font-size: 13px;
  line-height: 19px;
  .material-icons {
    flex-shrink: 0;
    font-size: 20px;
    color: ${(p) => (p.$tom === "aviso" ? "#B06000" : "#1264C7")};
  }
`;

export const Check = styled.span<{ $ok?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #3c4043;
  .material-icons {
    font-size: 18px;
    color: ${(p) => (p.$ok ? "#1E8E3E" : "#F9AB00")};
  }
`;

export const Badge = styled.span<{ $tom?: "azul" | "verde" | "amarelo" | "cinza" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${(p) =>
    p.$tom === "verde" ? "#E6F4EA" : p.$tom === "amarelo" ? "#FEF7E0" : p.$tom === "cinza" ? "#F1F3F4" : "#EAF1FB"};
  color: ${(p) =>
    p.$tom === "verde" ? "#137333" : p.$tom === "amarelo" ? "#B06000" : p.$tom === "cinza" ? "#5F6368" : "#1264C7"};
  .material-icons {
    font-size: 14px;
  }
`;

/** Campo da prancheta (.inp): caixa de 48px com prefixo, ícone e conteúdo sem borda. */
export const Campo = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 14px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
  min-width: 0;
  &:hover {
    border-color: #9aa0a6;
  }
  &:focus-within {
    border-color: var(--brand);
    box-shadow: 0 0 0 1px var(--brand);
  }
  > .material-icons {
    color: #5f6368;
    font-size: 20px;
    flex-shrink: 0;
  }
  input,
  select {
    border: 0;
    outline: 0;
    flex: 1;
    font-size: 15px;
    font-family: inherit;
    color: #202124;
    background: transparent;
    min-width: 0;
    padding: 0;
    height: 100%;
  }
  select {
    appearance: none;
    cursor: pointer;
  }
  input::placeholder {
    color: #9aa0a6;
  }
`;

export const Prefixo = styled.span`
  font-size: 14px;
  color: #5f6368;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const Ponto = styled.span<{ $cor: string }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${(p) => p.$cor};
  flex-shrink: 0;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 6px 0 12px;
  border-radius: 999px;
  background: #eaf1fb;
  color: #0b4fa8;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  max-width: 100%;
  .texto {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const ChipX = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #0b4fa8;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  &:hover {
    background: #d6e4f7;
  }
  .material-icons {
    font-size: 16px;
  }
`;

/** Chip de sugestão (borda tracejada) que adiciona ao clicar. */
export const ChipSugestao = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px dashed #c2d7f5;
  color: #1264c7;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #f8fafd;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .material-icons {
    font-size: 16px;
  }
`;

/** Chip "fantasma" com campo de busca dentro (prancheta: "Pesquisar categoria para adicionar"). */
export const ChipBusca = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px dashed #c2d7f5;
  color: #1264c7;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: text;
  .material-icons {
    font-size: 16px;
  }
  input {
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 13px;
    font-family: inherit;
    width: 240px;
    max-width: 50vw;
    color: #202124;
  }
  input::placeholder {
    color: #1264c7;
    opacity: 0.8;
  }
`;

export const ChipBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  background: #fff;
  min-width: 0;
`;

export const BotaoPequeno = styled.button<{ $variante?: "tonal" | "outlined" | "filled" | "text" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${(p) => (p.$variante === "outlined" ? "#DADCE0" : "transparent")};
  background: ${(p) =>
    p.$variante === "filled" ? "#1264C7" : p.$variante === "tonal" ? "#EAF1FB" : p.$variante === "text" ? "transparent" : "#fff"};
  color: ${(p) => (p.$variante === "filled" ? "#fff" : p.$variante === "text" ? "#5F6368" : "#0B4FA8")};
  &:hover {
    filter: brightness(0.97);
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
    filter: none;
  }
  .material-icons {
    font-size: 18px;
  }
`;

export const Botao = styled(BotaoPequeno)`
  height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  .material-icons {
    font-size: 20px;
  }
`;

export const LinkAcao = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  padding: 0;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: #1264c7;
  cursor: pointer;
  .material-icons {
    font-size: 16px;
  }
  &:hover {
    color: #0b4fa8;
  }
`;

/** Cabeçalho de seção: título 22px + apoio + extra à direita (selo, botão). */
export const CabecalhoDaSecao: React.FC<{ titulo: React.ReactNode; apoio?: React.ReactNode; extra?: React.ReactNode }> = ({ titulo, apoio, extra }) => (
  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 24 }}>
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <h3 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 22, fontWeight: 500, color: "var(--ink)", display: "flex", alignItems: "center", gap: 10 }}>{titulo}</h3>
      {apoio && <Hint>{apoio}</Hint>}
    </div>
    {extra}
  </div>
);

export const Completude: React.FC<{ perfil: Profile }> = ({ perfil }) => {
  const itens = itensDeCompletude(perfil);
  const feitos = itens.filter((i) => i.ok).length;
  const pct = Math.round((feitos / itens.length) * 100);
  return (
    <AsideCard>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Lab>Completude do perfil</Lab>
        <span style={{ fontFamily: "var(--font-sans)", fontWeight: 500, color: pct >= 80 ? "#137333" : pct >= 50 ? "#B06000" : "#B3261E" }}>{`${pct}%`}</span>
      </div>
      <div style={{ height: 8, borderRadius: 999, background: "#F1F3F4", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: pct >= 80 ? "#1E8E3E" : pct >= 50 ? "#F9AB00" : "#D93025", borderRadius: 999, transition: "width .3s" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
        {itens.map((item) => (
          <Check key={item.rotulo} $ok={item.ok}>
            <span className="material-icons">{item.ok ? "check_circle" : "radio_button_unchecked"}</span>
            {item.rotulo}
          </Check>
        ))}
      </div>
    </AsideCard>
  );
};

/** Cartão "Como aparece no Google" com nome, nota, categoria, endereço e horário de hoje. */
export const PreviaNoGoogle: React.FC<{ perfil: Profile }> = ({ perfil }) => {
  const endereco = [perfil.address, [perfil.city, perfil.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
  const hoje = resumoDeHoje(perfil.hours);
  const estrelas = perfil.rating ? "★".repeat(Math.round(perfil.rating)) : "";
  return (
    <AsideCard style={{ gap: 14 }}>
      <Lab>Como aparece no Google</Lab>
      <div style={{ display: "flex", gap: 12 }}>
        <span className="material-icons" style={{ width: 64, height: 64, borderRadius: 12, background: "#EAF1FB", color: "#0B4FA8", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 30, flexShrink: 0 }}>storefront</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 500, overflowWrap: "anywhere" }}>{perfil.name || "Nome da empresa"}</span>
          <Hint>
            {perfil.rating ? `${perfil.rating.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${estrelas}${perfil.reviewCount ? ` (${perfil.reviewCount})` : ""} · ` : ""}
            {perfil.category || "Categoria não informada"}
          </Hint>
          {endereco && <Hint>{endereco}</Hint>}
          {hoje && <span style={{ fontSize: 12, color: hoje.aberto ? "#137333" : "#B3261E" }}>{hoje.texto}</span>}
        </div>
      </div>
    </AsideCard>
  );
};
